import React from "react";
import { Button } from "../ui/button";
import {
  ChefHat, Sparkles, Wine, Check, Archive, RotateCcw, Trash2, Pencil,
} from "lucide-react";

/**
 * Portrait dish card — grid style inspired by catálogo de comercio.
 *
 * Three states:
 *  - idle           → image + name + price, sutil chip en esquina si hay flag
 *  - selected       → borde primario + check + panel expandido con CTAs
 *                     (Ver/Editar, Archivar)
 *  - archived mode  → opacidad reducida, chip "Archivada" permanente,
 *                     CTAs Restaurar / Eliminar definitivamente cuando selected
 */
function resolveImageSource(dish) {
  if (!dish?.image_url) return null;
  if (dish.image_source === "stock" || dish.image_url.includes("/api/images/stock_")) return "stock";
  if (dish.image_source === "ai") return "ai";
  if (dish.image_source === "manual") return "manual";
  if (dish.source === "universal_beverage" || dish.is_commercial_beverage === true) return "stock";
  if (dish.image_url.includes("/api/images/dish_")) return "ai";
  if (dish._isBeverage) return "stock";
  return null;
}

const DishCard = React.memo(function DishCard({
  dish,
  hasRecipe = null,
  isSelected = false,
  onSelect,
  onView,
  onArchive,
  isArchivedView = false,
  onRestore,
  onDelete,
  busy = false,
}) {
  const imgSource = resolveImageSource(dish);
  const isBeverage = dish._isBeverage === true;
  const showNoRecipeChip = hasRecipe === false && !isBeverage && !isArchivedView;
  const PlaceholderIcon = isBeverage ? Wine : ChefHat;

  const handleCardClick = (e) => {
    // Clicking the CTAs themselves shouldn't toggle selection
    if (e.target.closest("[data-card-cta]")) return;
    onSelect?.(dish);
  };

  return (
    <div
      className={[
        "group relative flex flex-col rounded-2xl overflow-hidden border transition-all cursor-pointer select-none",
        "bg-card dark:bg-neutral-900",
        isSelected
          ? "border-primary ring-2 ring-primary/30 shadow-lg"
          : "border-border dark:border-neutral-800 hover:border-slate-300 hover:shadow-md",
        isArchivedView && !isSelected ? "opacity-70" : "",
      ].join(" ")}
      onClick={handleCardClick}
      data-testid={`recipe-card-${dish.dish_id}`}
      data-selected={isSelected ? "true" : "false"}
    >
      {/* Image / placeholder — aspect portrait 3:4 */}
      <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 dark:from-neutral-800 dark:to-neutral-900">
        {dish.image_url ? (
          <img
            src={dish.image_url}
            alt={dish.name}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <PlaceholderIcon className="w-14 h-14 text-orange-300/80" strokeWidth={1.4} />
          </div>
        )}

        {/* Top-left chips (flags) */}
        <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
          {showNoRecipeChip && (
            <span
              className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100/95 text-amber-800 border border-amber-200/80 backdrop-blur-sm font-medium"
              data-testid={`recipe-card-no-recipe-${dish.dish_id}`}
            >
              Sin receta
            </span>
          )}
          {isArchivedView && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800/85 text-white border border-slate-700 backdrop-blur-sm font-medium">
              Archivada
            </span>
          )}
        </div>

        {/* Top-right tiny source hint */}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          {imgSource === "ai" && !isSelected && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-600/80 text-white backdrop-blur-sm font-medium flex items-center gap-0.5">
              <Sparkles className="w-2.5 h-2.5" /> IA
            </span>
          )}
          {isSelected && (
            <span
              className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center shadow-md"
              data-testid={`dish-selected-check-${dish.dish_id}`}
            >
              <Check className="w-4 h-4" strokeWidth={3} />
            </span>
          )}
        </div>
      </div>

      {/* Name + price */}
      <div className="px-3 pt-3 pb-3 flex flex-col gap-1">
        <h2 className="text-sm font-semibold leading-tight text-foreground line-clamp-2 min-h-[2.4em]">
          {dish.name}
        </h2>
        <span className="text-xs text-muted-foreground font-medium">
          {dish.price != null ? `${Number(dish.price).toFixed(2)} €` : "—"}
        </span>

        {/* Expanded CTA panel — only when selected */}
        {isSelected && (
          <div className="mt-2 pt-2 border-t border-dashed border-primary/30 flex flex-col gap-1.5" data-card-cta>
            {!isArchivedView ? (
              <>
                <Button
                  size="sm"
                  className="w-full h-8 text-xs"
                  onClick={() => onView?.(dish)}
                  disabled={busy}
                  data-testid={`view-recipe-${dish.dish_id}`}
                >
                  {isBeverage ? (
                    <>
                      <Pencil className="w-3.5 h-3.5 mr-1.5" />
                      Ver / Editar bebida
                    </>
                  ) : (
                    <>
                      <ChefHat className="w-3.5 h-3.5 mr-1.5" />
                      Ver / Editar receta
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full h-7 text-[11px] text-muted-foreground hover:text-foreground"
                  onClick={() => onArchive?.(dish)}
                  disabled={busy}
                  data-testid={`archive-dish-${dish.dish_id}`}
                >
                  <Archive className="w-3 h-3 mr-1.5" />
                  Archivar
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => onRestore?.(dish)}
                  disabled={busy}
                  data-testid={`restore-dish-${dish.dish_id}`}
                >
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                  Restaurar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full h-7 text-[11px] text-red-700 border-red-200 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/30"
                  onClick={() => onDelete?.(dish)}
                  disabled={busy}
                  data-testid={`delete-dish-${dish.dish_id}`}
                >
                  <Trash2 className="w-3 h-3 mr-1.5" />
                  Eliminar definitivamente
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default DishCard;
